import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { Camera, RotateCcw, Check, X, SwitchCamera } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface CameraCaptureProps {
  open: boolean;
  onCapture: (photoDataUrl: string) => void;
  onClose: () => void;
  title?: string;
}

type CameraFacing = 'user' | 'environment';
type CaptureState = 'preview' | 'captured' | 'error';

// getUserMedia requires a secure context (HTTPS or localhost).
// On iOS Safari over HTTP (local IP), we fall back to the native file picker.
function useNativeFilePicker(): boolean {
  return !window.isSecureContext || !navigator.mediaDevices?.getUserMedia;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ open, onCapture, onClose, title }) => {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFilePicker = useNativeFilePicker();

  const [captureState, setCaptureState] = useState<CaptureState>('preview');
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<CameraFacing>('user');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // ── Native file picker (iOS Safari over HTTP) ─────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) { onClose(); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setCapturedPhoto(dataUrl);
      setCaptureState('captured');
    };
    reader.readAsDataURL(file);
    // Reset so same file can be re-selected if user retakes
    e.target.value = '';
  };

  const triggerFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (open && isFilePicker) {
      setCapturedPhoto(null);
      setCaptureState('preview');
      // Auto-open native camera picker immediately
      setTimeout(() => fileInputRef.current?.click(), 100);
    }
  }, [open, isFilePicker]);

  // ── getUserMedia (non-iOS / HTTPS) ────────────────────────────────────────
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async (facing: CameraFacing) => {
    stopStream();
    setErrorMessage('');
    setCaptureState('preview');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      // Check for multiple cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access denied';
      if (msg.includes('Permission') || msg.includes('NotAllowed')) {
        setErrorMessage(t.cameraPermDenied);
      } else if (msg.includes('NotFound') || msg.includes('DevicesNotFound')) {
        setErrorMessage(t.cameraNotFound);
      } else {
        setErrorMessage(t.cameraAccessFailed);
      }
      setCaptureState('error');
    }
  }, [stopStream]);

  useEffect(() => {
    if (isFilePicker) return;
    if (open) {
      setCapturedPhoto(null);
      setCaptureState('preview');
      void startCamera(facingMode);
    } else {
      stopStream();
    }
    return () => { stopStream(); };
  }, [open]);

  const handleCapture = (): void => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedPhoto(dataUrl);
    setCaptureState('captured');
    stopStream();
  };

  const handleRetake = (): void => {
    setCapturedPhoto(null);
    if (isFilePicker) {
      setCaptureState('preview');
      setTimeout(() => fileInputRef.current?.click(), 100);
    } else {
      void startCamera(facingMode);
    }
  };

  const handleConfirm = (): void => {
    if (capturedPhoto) onCapture(capturedPhoto);
  };

  const handleSwitchCamera = (): void => {
    const next: CameraFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    void startCamera(next);
  };

  const handleClose = (): void => {
    stopStream();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:hidden"
        data-testid="camera-capture-dialog"
      >
        {/* ── Branded Gradient Header ── */}
        <div
          className="relative overflow-hidden px-5 py-4 premium-overlay"
          style={{ background: 'var(--gradient-primary)' }}
        >
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                <Camera className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">
                  ABSENSI
                </p>
                <p className="text-[15px] font-black text-white leading-tight">
                  {title ?? t.cameraTitle}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              aria-label="Close"
              data-testid="camera-close-btn"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        {/* Hidden file input for iOS Safari / HTTP fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* ── Camera / Preview Area ── */}
        <div className="relative bg-zinc-950" style={{ aspectRatio: '4/3' }}>
          {captureState === 'error' ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center"
              style={{ background: 'linear-gradient(135deg, #1c0800 0%, #2d1200 100%)' }}
            >
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full border-2"
                style={{ background: 'hsl(var(--destructive) / 0.2)', borderColor: 'hsl(var(--destructive) / 0.5)' }}
              >
                <X className="h-8 w-8 text-destructive" />
              </div>
              <div>
                <p className="text-sm font-bold text-white mb-3">{errorMessage}</p>
                <button
                  onClick={() => void startCamera(facingMode)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white border border-white/30 bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {t.tryAgain}
                </button>
              </div>
            </div>
          ) : captureState === 'captured' && capturedPhoto ? (
            <>
              <img
                src={capturedPhoto}
                alt="Captured"
                className="w-full h-full object-cover"
                data-testid="captured-photo-preview"
              />
              {/* Success badge */}
              <div
                className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white shadow-lg"
                style={{ background: 'hsl(var(--success))' }}
              >
                <Check className="h-4 w-4 text-white" />
              </div>
            </>
          ) : isFilePicker ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/20 bg-white/10 animate-pulse">
                <Camera className="h-8 w-8 text-white/60" />
              </div>
              <p className="text-sm text-white/60 font-medium">{t.openingCamera}</p>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
              data-testid="camera-video-feed"
            />
          )}

          <canvas ref={canvasRef} className="hidden" />

          {/* Selfie oval guide overlay */}
          {!isFilePicker && captureState === 'preview' && facingMode === 'user' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="w-32 h-44 rounded-[50%] border-2 border-white/50"
                style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.22)' }}
              />
            </div>
          )}

          {/* Switch camera — frosted glass pill */}
          {!isFilePicker && captureState === 'preview' && hasMultipleCameras && (
            <button
              onClick={handleSwitchCamera}
              className="absolute top-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white backdrop-blur-sm hover:bg-black/60 transition-all shadow-lg"
              aria-label={t.switchCamera}
              data-testid="switch-camera-btn"
            >
              <SwitchCamera className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* ── Bottom Action Bar ── */}
        <div className="bg-card px-5 py-4">
          {captureState === 'preview' && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 rounded-xl h-11"
                data-testid="camera-cancel-btn"
              >
                <X className="h-4 w-4 mr-1.5" />
                {t.cancel}
              </Button>
              <button
                onClick={isFilePicker ? triggerFilePicker : handleCapture}
                className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-lg active:scale-[0.98] transition-transform"
                style={{ background: 'var(--gradient-primary)' }}
                data-testid="capture-photo-btn"
              >
                <Camera className="h-4 w-4" />
                {isFilePicker ? t.openCamera : t.capturePhoto}
              </button>
            </div>
          )}

          {captureState === 'captured' && (
            <div className="space-y-3">
              <p className="text-center text-[11px] text-muted-foreground font-medium">{t.cameraModalDesc}</p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleRetake}
                  className="flex-1 rounded-xl h-11"
                  data-testid="retake-photo-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" />
                  {t.retakePhoto}
                </Button>
                <button
                  onClick={handleConfirm}
                  className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-lg active:scale-[0.98] transition-transform"
                  style={{ background: 'var(--gradient-primary)' }}
                  data-testid="confirm-photo-btn"
                >
                  <Check className="h-4 w-4" />
                  {t.usePhoto}
                </button>
              </div>
            </div>
          )}

          {captureState === 'error' && (
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full rounded-xl h-11"
            >
              {t.close}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCapture;
